
import React, { useState, forwardRef, useEffect, useMemo, useCallback, useRef } from 'react';
import { Row, Spin, Form, Checkbox, Typography, Tooltip, Collapse, Input, Col, List, Dropdown, Select, Popover } from 'antd';
import {HashRouter as Router} from 'react-router-dom';
import { useForm } from 'antd/lib/form/Form';
import { MODAL_SIZE_EXTRA_LARGE, BASE_GUTTER } from 'constants/Layout';
import _, { noop, isEmpty, uniq, cloneDeep, isUndefined, isNumber, find, map, difference, uniqBy, get, compact, sortBy } from 'lodash';
import { SearchIcon, CloseIcon, DeleteIcon, EditPencilIcon } from "components/Icons";
import { CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';

import BaseModal from 'components/BaseModal';
import { FieldTypes, nameRegexValidator, parseSelectOptions, emailValidator, generateFormFields } from "utils/formUtils";
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import BusinessUnitAPI from 'restAPI/BusinessUnitAPI';
import ProductAPI from 'restAPI/ProductAPI';
import TeamsAPI from 'restAPI/TeamsAPI';
import UserAPI from 'restAPI/UserAPI';
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import AppLoader from 'components/AppLoader';
import message from 'components/CustomMessage';
import { PermissionSetMap, ROLE_SUPERADMIN, ROLE_TENANTADMIN, Admin } from 'constants/RBACConstants';
import { isSuperOrTenantAdminOrTenantRole, hasPermission } from 'utils/authService';
import { checkAvailableOptionsAreSelected, getRoleOptions } from 'views/IAM/Users/UserFormV2';
import useEnvironmentVariables from "hooks/useEnvironmentVariables"
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import SubProductAPI from "restAPI/SubProductAPI"
import { CellRenderers } from 'utils/tableUtils'
import User from 'components/User';
import TeamProductSubProductAccessField, { ACCESS_TYPE_GROUPS, ACCESS_TYPE_INDIVIDUAL } from './TeamProductSubProductAccessField';
import ApprovalWorkflow from './ApprovalWorkflow';
import { useAutoFocus } from 'hooks/useFocusHelpers';

const initialValues = {
  name: undefined,
  description: '',
  emailAlias: '',
  workspaceId: undefined,
  channels: undefined,
  msTeamsChannel: undefined,
  msTeamsTeam: undefined,
  msTeamsLoginId: undefined,
  properties: [{
    businessUnitId: undefined,
    businessUnitName: undefined,
    productSubProductMap: [],
    accessOnAllProduct: undefined,
    accessType: ACCESS_TYPE_INDIVIDUAL
  }],
  members: [],
  accessOnAllBusinessUnits: undefined,
  securityOwner: undefined,
  complianceOwner: undefined,
  engineeringOwner: undefined,
  supportOwner: undefined,
  businessOwner: undefined
}

const setTeamsValues = team => {
  const data = cloneDeep(initialValues);
  data.name = team.name;
  data.description = team.description;
  data.emailAlias = team.emailAlias;
  data.workspaceId = team?.slackConfigId?.workspace;
  data.securityOwner = team.securityOwner?.id;
  data.complianceOwner = team.complianceOwner?.id;
  data.engineeringOwner = team.engineeringOwner?.id;
  data.supportOwner = team.supportOwner?.id;
  data.businessOwner = team.businessOwner?.id;
  data.accessOnAllBusinessUnits = team.accessOnAllBusinessUnits;
  data.msTeamsLoginId = team.msTeamsLoginId;
  data.msTeamsTeam = team?.msTeamsTeam?.id
  data.msTeamsChannel = _.map(team.msTeamsChannel, channel => channel.id);
  if (data.accessOnAllBusinessUnits) {
    data.properties = [];
  }
  if (team.properties && team.properties.length && !data.accessOnAllBusinessUnits) {
    data.properties = team.properties.map(p => {
      return {
        id: p.id,
        businessUnitId: p.businessUnit?.id,
        businessUnitName: p.businessUnit?.name,
        productSubProductMap: (p.productSubProductMap || []).map(_p =>  {
          return {
            product: +_p.product.id,
            subProduct: (_p.subProducts || []).map(sub => +sub.id),
            accessOnAllSubProduct: _p.accessOnAllSubProduct || false
          }
        }),
        accessOnAllProduct: p.accessOnAllProduct,
        groups: p.groups,
        accessType: _.isEmpty(p.groups) ? ACCESS_TYPE_INDIVIDUAL : ACCESS_TYPE_GROUPS,
        members: team.members || []
      }
    })
  }
  if (team.members && team.members.length) {
    data.members = team.members.map(m => {
      return {
        ...m,
        user: m.user?.id,
        role: m.role?.name
      }
    });
  }
  data.approvers = [];
  _.sortBy(team.approvalWorkflow?.approvers, "sequence").forEach(entry => {
      data.approvers[entry.sequence] = entry.reviewerId || entry.externalReviewerEmail;
  });
  data.approvers = _.compact(data.approvers);
  data.subscriberUserIds = team.approvalWorkflow?.subscriberUserIds;
  if(!isEmpty(team?.slackConfigId?.channels)){
    data.channels = Object.keys(team.slackConfigId.channels);
  }
  return data;
}

const KEYS = ['securityOwner', 'complianceOwner', 'engineeringOwner', 'supportOwner', 'businessOwner'];

export default forwardRef(function UserForm({ afterClose = noop, team, activeUser, scope="TENANT", ...restProps }, ref) {
  const featureFlags = useFeatureFlag();
  const [loading, setLoading] = useState(false);
  const [form] = useForm();
  const initialFormValues = useMemo(() => (!isEmpty(team) ? setTeamsValues(team) : _.cloneDeep(initialValues)), [team]);
  const editMode = !isEmpty(team);
  const [buOptions, setBuOptions] = useState([]);
  const [selectedBu, setSelectedBu] = useState([]);
  const [slackWorkspaceOptions, setSlackWorkspaceOptions] = useState([]);
  const [slackChannelOptions, setSlackChannelOptions] = useState([]);
  const [slackChannelLoadingState, setSlackChannelLoadingState] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(team?.slackConfigId?.workspace);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [accessOnAllBusinessUnits, setAccessOnAllBusinessUnits] = useState(initialFormValues.accessOnAllBusinessUnits);
  const [userOptions, setUserOptions] = useState();
  const [teamUserOptions, setTeamUserOptions] = useState([]);
  const [tenantuserOptions, setTenantUserOptions] = useState([]);
  const [formData, setFormData] = useState(initialFormValues)
  const [msTeamConfigOptions, setMsTeamConfigOptions] = useState([]);
  const [selectedMsTeamsConfigId, setSelectedMsTeamsConfigId] = useState(team?.msTeamsLoginId);
  const [msTeamOptions, setMsTeamOptions] = useState([]);
  const [selectedMsTeamsId, setSelectedMsTeamsId] = useState(team?.msTeamsTeam?.id);
  const [msTeamsChannelsOptions, setMsTeamsChannelsOptions] = useState([]);
  const [msTeamLoadingState, setMsTeamLoadingState] = useState(false);
  const [msTeamChannelLoadingState, setMsTeamChannelLoadingState] = useState(false)

  const createdTeam = useRef({});
  const productsAndSubProductsRef = useRef({});
  const approversRef = useRef([]);
  const isEditingRef = useRef({});
  const teamInputFieldRef = useAutoFocus();

  const activeUserProducts = useMemo(() => {
    const productSubProductMap = {};
    if (!isEmpty(team?.properties)) {
      team.properties.forEach((property, i) => {
        productSubProductMap[property.businessUnit?.id || i] = (property.productSubProductMap || []).map(obj => {
          const { product, subProducts=[], accessOnAllSubProduct} = obj;
          return { product: +product.id, subProduct: subProducts.map(sub => +sub.id), accessOnAllSubProduct }
        });
      })
    }
    return productSubProductMap;
	}, [team]);

	const activeUserBUs = useMemo(() => {
    const bu = {};
    if (!isEmpty(team?.properties)) {
      if (!isEmpty(team?.properties)) {
        team.properties.forEach((property, i) => {
          bu[i] = [property.businessUnit];
        })
      }
    }
    return bu;
	}, [team]);

  const activeUserOptions = useMemo(() => {
    if (_.isUndefined(userOptions)) return [];
    const options = [...userOptions];
    [...KEYS, 'members'].forEach(key => {
      if (!isEmpty(team?.[key])) {
        if (key === 'members') {
           const selectedMembers = [];
           team.members.forEach(m => {
             if (m.user) {
              options.push({ id: m.user.id, name: m.user.name });
              selectedMembers.push(m.user.id)
             }
           })
           setSelectedMembers(selectedMembers)
        } else {
          const name = team[key].displayName || team[key].name;
          options.push({ id: team[key].id, name})
        }
      }
    })
    return uniqBy(options, 'id');
	}, [team, userOptions, setSelectedMembers]);

  const activeUserRolesMap = useCallback(
    (options) => {
      const mapping = {};
      options.forEach((option, i) => {
        if (option.user && option.role) {
          mapping[option.user.id] = {...option.role, role: option.role.name};
        }
      })
      return mapping
  }, [])

  const activeUserRole = useMemo(() => {
		return !isEmpty(team?.members)
      ?  activeUserRolesMap(team.members || [])
      : [];
	}, [team, activeUserRolesMap]);
  
  useEffect(() => {
    setLoading(true);
    const params = {}
    if (!isEmpty(team)) {
      params.teamId = team.id;
    }
    const promiseArr = [
      BusinessUnitAPI.getBUListWithAdminAccess().promise,
      ToolsIntegrationsAPI.getSlackWorkSpaceDetails(scope).promise,
      UserAPI.getAssignableRoles().promise,
      UserAPI.getAll(undefined, 'TEAM').promise,
      UserAPI.getAll(undefined, 'TENANT').promise,
      ToolsIntegrationsAPI.getMSTeamsLoginDetails(scope).promise
    ];
    Promise.allSettled(promiseArr).then(responses => {
      const [buOpts, workspaceOpts, roleOpts, teamUserOpts, tenantUserOpts, msTeamConfigOpts] = responses;
      setBuOptions( buOpts.value || []);
      setRoleOptions(roleOpts.value || []);

      const opts = sortBy((uniqBy([...teamUserOpts.value, ...tenantUserOpts.value], "id") || []).map(user => ({ ...user, label: user.name || user.displayName, value: user.id })), 'label');
      setUserOptions(opts);

      const teamOpts = sortBy((teamUserOpts.value|| []).map(user => ({ ...user, label: user.name || user.displayName, value: user.id })), 'label');
      setTeamUserOptions(teamOpts);

      const tenantOpts = sortBy((tenantUserOpts.value || []).map(user => ({ ...user, label: user.name || user.displayName, value: user.id })), 'label');
      setTenantUserOptions(tenantOpts);

      const selected = (team && team.members.map(m => m.user?.id)) || [];
      setSelectedMembers(compact(selected));
      const parsedSlackWorkspace = (workspaceOpts.value || []).map((workspace) => {
        const {
          id,
          teamName,
          ...restProps
        } = workspace;
        return {
          label: teamName,
          value: id,
          ...restProps
        };
      });

      const parsedMsTeamConfig = (msTeamConfigOpts.value || []).map((teamConfig) => {
        const {
          id,
          name,
          ...restProps
        } = teamConfig;
        return {
          label: name,
          value: id,
          ...restProps
        };
      });
      setSlackWorkspaceOptions(parsedSlackWorkspace);
      setMsTeamConfigOptions(parsedMsTeamConfig)
      setLoading(false);
    })
  }, [team, scope, activeUser]);

  const resetSlackChannelsField = useCallback(() => {
    form.setFieldsValue({channels: undefined});
  },[form]);

  const resetMsTeamField = useCallback(() => {
    form.setFieldsValue({msTeamsTeam: undefined});
  }, [form])

  const resetMsTeamChannelField = useCallback(() => {
    form.setFieldsValue({msTeamsChannel: []});
  }, [form])

  const onClearWorkspaceId = useCallback(() => {
    resetSlackChannelsField();
    setSelectedWorkspaceId(undefined);
  },[resetSlackChannelsField]);

  const onClearMsTeamConfigId = useCallback(() => {
    resetMsTeamField();
    resetMsTeamChannelField();
    setSelectedMsTeamsConfigId(undefined);
    setSelectedMsTeamsId(undefined);
  },[resetMsTeamField, resetMsTeamChannelField])

  const onClearMsTeam = useCallback(() => {
    resetMsTeamChannelField();
    setSelectedMsTeamsId(undefined)
  },[resetMsTeamChannelField])

  useEffect(() => {
    if(isUndefined(selectedWorkspaceId)){
      return;
    }
    setSlackChannelLoadingState(true);
    const {promise, cancel} = ToolsIntegrationsAPI.getSlackChannelsWithScope(selectedWorkspaceId, scope, false);
    promise.then(response => {
      const parsedChannelOptions = response.map(({channelId: value, channelName: label}) => ({label, value}));
      setSlackChannelOptions(parsedChannelOptions);
      setSlackChannelLoadingState(false);
    }).catch(err => {
      setSlackChannelLoadingState(false);
      message.warn('Your Slack configuration may have been removed! Please review and save changes.')
      form.setFieldsValue({
        workspaceId: undefined,
      });
      onClearWorkspaceId()
    });
    return cancel;
  },[selectedWorkspaceId, form, onClearWorkspaceId, scope]);

  useEffect(() => {
    if(!isNumber(selectedMsTeamsConfigId)){
      return;
    }
    setMsTeamLoadingState(true);
    const {promise, cancel} = ToolsIntegrationsAPI.getMSTeams(selectedMsTeamsConfigId, scope);
    promise.then(response => {
      const parsedTeamOptions = response.map(({id: value, name: label}) => ({label, value}));
      setMsTeamOptions(parsedTeamOptions);
      setMsTeamLoadingState(false);
    }).catch(err => {
      setMsTeamLoadingState(false);
      message.warn('Your MS Team configuration may have been removed! Please review and save changes.')
      form.setFieldsValue({
        msTeamsLoginId: undefined,
      });
      onClearMsTeamConfigId()
    });
    return cancel;
  },[selectedMsTeamsConfigId, form, onClearMsTeamConfigId, scope]);

  useEffect(() => {
    if(isUndefined(selectedMsTeamsId) || isUndefined(selectedMsTeamsConfigId)){
      return;
    }
    setMsTeamChannelLoadingState(true);
    const {promise, cancel} = ToolsIntegrationsAPI.getMSTeamsChannel(selectedMsTeamsConfigId, selectedMsTeamsId, scope);
    promise.then(response => {
      const parsedTeamOptions = response.map(({id: value, name: label}) => ({label, value}));
      setMsTeamsChannelsOptions(parsedTeamOptions);
      setMsTeamChannelLoadingState(false);
    }).catch(err => {
      setMsTeamChannelLoadingState(false);
      message.warn('Your MS Team configuration may have been removed! Please review and save changes.')
      form.setFieldsValue({
        msTeamsLoginId: undefined,
        msTeamsTeam: undefined
      });
      onClearMsTeamConfigId()
    });
    return cancel;
  },[selectedMsTeamsConfigId, selectedMsTeamsId, form, onClearMsTeamConfigId, scope]);

  const setBUMappingOptions = useCallback((properties=[]) => {
    const mapping = {};
    properties.forEach(o => {
      if (o.businessUnitId) {
        mapping[o.businessUnitId] = [];
      }
    });

    const keys = Object.keys(mapping);
    setSelectedBu(uniq(keys.map(m => +m)));
  }, []);

  useEffect(() => {
    if (team) {
      setBUMappingOptions(setTeamsValues(team)?.properties)
    }
  }, [setBUMappingOptions, team ]);

  const handleBUChange = useCallback(
    (value, field) => {
      const { name } = field;
      const { resetSelectedData } = productsAndSubProductsRef.current[name] || {}
      const newValues = form.getFieldValue("properties") || [];
      const optionSelected = buOptions.find(o => o.id === value);
      selectedBu[name] = value;
      setSelectedBu(uniq(selectedBu));

      if (newValues[name]) {
        resetSelectedData && resetSelectedData(optionSelected.id)
        newValues[name].productSubProductMap = [];
        newValues[name].businessUnitName = optionSelected ? optionSelected.name : undefined;
      }
      form.setFieldsValue({ properties: newValues });
    },
    [form, buOptions, selectedBu],
  )

  const handleCheckBoxChange = useCallback(
    (event) => {
      const { target } = event;
      const {listField: { key }} = target;
      const { resetSelectedData } = productsAndSubProductsRef.current[key] || {}
      const newValues = form.getFieldValue("properties") || [];
      if (newValues[key]) {
        const businessUnitId = newValues[key].businessUnitId;
        resetSelectedData && resetSelectedData(businessUnitId)
        newValues[key].productSubProductMap = [];
        newValues[key].groups = [];
      }
      form.setFieldsValue({ properties: newValues });
      setFormData((formData) => ({ ...formData, properties: newValues }));
    },
    [form, productsAndSubProductsRef],
  )
  const onAccessTypeChange = useCallback(
    (accessType, key) => {
      const { resetSelectedData } = productsAndSubProductsRef.current[key] || {}
      const newValues = form.getFieldValue("properties") || [];
      if (newValues[key]) {
        const businessUnitId = newValues[key].businessUnitId;
        resetSelectedData && resetSelectedData(businessUnitId)
        newValues[key].productSubProductMap = [];
        newValues[key].accessType = accessType;
      }
      form.setFieldsValue({ properties: newValues });
      setFormData((formData) => ({ ...formData, properties: newValues }));
    },
    [form, productsAndSubProductsRef],
  )
  const onGroupSelect = useCallback(
    (groups, key) => {
      const newValues = form.getFieldValue("properties") || [];
      if (newValues[key]) {
        newValues[key].groups = groups;
        newValues[key].productSubProductMap = [];
        newValues[key].accessOnAllProduct = false
        form.setFieldsValue({ properties: newValues });
        setFormData((formData) => ({ ...formData, properties: newValues }));
      }
    },
    [form],
  )

  const onRemoveItem = useCallback(
    () => {
      const configs = compact(form.getFieldValue("properties") || []);
      const selectedBUs = configs.map(config => config.businessUnitId)
      setSelectedBu(selectedBUs)
  }, [form]);

  const onRemoveMembers = useCallback(
    () => {
      const data = cloneDeep(formData)
      const members = compact(form.getFieldValue("members"));
      const selectedMembers = members.map(member => member.user)
      setFormData({...data, members});
      setSelectedMembers(selectedMembers)
  }, [form, formData]);

  const handleWorkspaceChange = useCallback(async (workspaceId) => {
    try{
      resetSlackChannelsField();
      setSelectedWorkspaceId(workspaceId);
    }catch(err){
      console.error('Err. handleWorkspaceChange: ',err);
    }
  },[resetSlackChannelsField]);

  const handleMsTeamConfigChange = useCallback(async (configId) => {
    try{
      resetMsTeamField();
      resetMsTeamChannelField();
      setSelectedMsTeamsConfigId(configId);
    }catch(err){
      console.error('Err. handleMsTeamConfigChange: ',err);
    }
  },[resetMsTeamField, resetMsTeamChannelField]);

  const handleMsTeamChange = useCallback(async(teamId) => {
    try{
      resetMsTeamChannelField();
      setSelectedMsTeamsId(teamId);
    }catch(err){
      console.error('Err. handleMsTeamChange: ',err);
    }
  },[resetMsTeamChannelField])

  const handleChannelSelection = useCallback((value) => {
    if(Array.isArray(value) && value.length){
      const channelId = value[value.length -1];
      form.setFieldsValue({channels: [channelId]});
    }
  },[form]);

  const handleMemberChange = useCallback(({ value, index, type }) => {
    const data = cloneDeep(formData)
    if (type === 'user') {
      selectedMembers.push(value)
      setSelectedMembers(compact(uniq(selectedMembers)));
    }
    if (!data.members[index]) {
      data.members[index] = {}
    }
    data.members[index][type] = value;
    if (!data.members[index].hasOwnProperty('disableLogin')) {
      data.members[index].disableLogin = false;
    }
    setFormData({...data, members: compact(data.members)});
  }, [selectedMembers, setSelectedMembers, formData])

  const handleAllBusinessUnit = useCallback(e => {
    const value = e.target.checked;
    setAccessOnAllBusinessUnits(value);
    let properties;
    if (value) {
      properties = [];
      setSelectedBu([]);
    } else {
      properties = [{
        businessUnitId: undefined,
        businessUnitName: undefined,
        productSubProductMap: [],
        accessOnAllProduct: undefined,
      }]
    }
    form.setFieldsValue({ properties });
  }, [setAccessOnAllBusinessUnits, form]);

  const productAndSubProductChange = useCallback((fieldData) => {
    const { data, name } = fieldData;
    //empty groups and set other form fields only when user has selected some
    //products or subproducts
    if(!isEmpty(data)){
      const properties = form.getFieldValue('properties')
      properties[name] = {
        ...properties[name],
        groups: [],
        productSubProductMap: Object.keys(data).map(key => {
          const subProduct = data[key];
          return {
            product: +key,
            subProduct
          }
        })
      }
      setFormData({...formData, properties})
      form.setFieldsValue({ properties });
    }
  }, [form, formData]);

  const setIsEditing = useCallback((userId, isEditing) => {
    if (isEditing) {
      isEditingRef.current[userId] = true;
    } else {
      delete isEditingRef.current[userId];
    }
  }, []);

  const teamsFormFields = useMemo(() => (
    [
      {
        name: 'name',
        colSpan: 8,
        label: <Vocabulary name="team" />,
        rules: [
          { required: true, 
            message: <><Vocabulary name="team" /> is required</>
          }, {
            validator: (rule, value) => {
              if (!editMode && !isEmpty(value)) {
                return validate(value).then(res => {
                  return res ? Promise.reject(<><Vocabulary name="team" /> already exists.</>) : Promise.resolve();
                });
              }
              return Promise.resolve();
            }
          },
          nameRegexValidator({ regex: /(?!^[\\d _&\\-]+$)^[.A-Za-z0-9]+[.A-Za-z0-9 ()&_\\\\/\\-]*$/g, fieldName: getVocabulary("team") })
        ],
        props: {
          ref: teamInputFieldRef
        }
      },
      {
        name: 'description',
        colSpan: 24,
        label: 'Description',
        type: FieldTypes.TEXT_AREA,
        placeholder: "Add description about teams"
      },
      {
        name: 'members',
        noStyle: false,
        render: () => <AddMembers 
            form={form}
            setIsEditing={setIsEditing}
            handleMemberChange={handleMemberChange}
            activeUserOptions={activeUserOptions}
            teamLevelUserOptions={teamUserOptions}
            selectedMembers={selectedMembers}
            roleOptions={roleOptions}
            activeUserRole={activeUserRole}
            onRemoveMembers={onRemoveMembers}
            formData={formData}
          />,
          rules: [{
            validator: (rule, value) => {
              if (isEmpty(isEditingRef.current)) {
                return Promise.resolve();
              }
              return Promise.reject('Please save selected members');
            }
          }]
      },
      {
        name: 'scopeOfAccess',
        render: () => <ScopeOfAccess 
            form={form}
            initialFormValues={initialFormValues}
            handleAllBusinessUnit={handleAllBusinessUnit}
            onRemoveItem={onRemoveItem}
            accessOnAllBusinessUnits={accessOnAllBusinessUnits}
            selectedBu={selectedBu}
            buOptions={buOptions}
            activeUserBUs={activeUserBUs}
            handleBUChange={handleBUChange}
            productsAndSubProductsRef={productsAndSubProductsRef}
            productAndSubProductChange={productAndSubProductChange}
            activeUserProducts={activeUserProducts}
            activeUser={activeUser}
            editMode={editMode}
            handleCheckBoxChange={handleCheckBoxChange}
            onAccessTypeChange={onAccessTypeChange}
            onGroupSelect={onGroupSelect}
          />
      },
      {
        name: 'ownerShip',
        render: () => <OwnerShip 
            form={form} 
            activeUserOptions={activeUserOptions}
            tenantLevelUserOptions={tenantuserOptions}
            selectedMembers={selectedMembers}
            initialFormValues={initialFormValues} 
            formData={formData} team={team}
            buOptions={buOptions}
          />
      },
      ...(featureFlags.EXCEPTION_MODULE ? [{
        name: 'approvalWorkflow',
        render: () => <ApprovalWorkflow
            form={form}
            initialFormValues={initialFormValues} 
            formData={formData}
            team={team}
            activeUserOptions={activeUserOptions}
            approversRef={approversRef}
          />
      }] : []),
      {
        name: 'communication',
        render: () => <Communication 
            form={form} 
            slackWorkspaceOptions={slackWorkspaceOptions} 
            slackChannelLoadingState={slackChannelLoadingState} 
            initialFormValues={initialFormValues} 
            handleChannelSelection={handleChannelSelection}
            onClearWorkspaceId={onClearWorkspaceId}
            onClearMsTeamConfigId={onClearMsTeamConfigId}
            handleWorkspaceChange={handleWorkspaceChange}
            handleMsTeamConfigChange={handleMsTeamConfigChange}
            slackChannelOptions={slackChannelOptions}
            msTeamConfigOptions={msTeamConfigOptions}
            onClearMsTeam={onClearMsTeam}
            handleMsTeamChange={handleMsTeamChange}
            msTeamOptions={msTeamOptions}
            msTeamLoadingState={msTeamLoadingState}
            msTeamsChannelsOptions={msTeamsChannelsOptions}
            msTeamChannelLoadingState={msTeamChannelLoadingState}
            featureFlags={featureFlags}
          />
      }
    ]
  ), [
    buOptions, selectedBu, handleCheckBoxChange, handleBUChange, form, editMode,
    activeUser, activeUserBUs, activeUserProducts, onRemoveItem, handleWorkspaceChange,
    slackChannelOptions, slackChannelLoadingState, slackWorkspaceOptions, onClearWorkspaceId,
    handleChannelSelection, onRemoveMembers, roleOptions, selectedMembers, accessOnAllBusinessUnits,
    handleAllBusinessUnit, handleMemberChange, activeUserRole, activeUserOptions, teamUserOptions, tenantuserOptions,
    productAndSubProductChange, initialFormValues, formData, team, msTeamConfigOptions,
    onClearMsTeamConfigId, handleMsTeamConfigChange, handleMsTeamChange, msTeamLoadingState,msTeamOptions, 
    onClearMsTeam, msTeamChannelLoadingState, msTeamsChannelsOptions,featureFlags,
    onAccessTypeChange, onGroupSelect, setIsEditing
  ]);

  const fields = useMemo(() => {
    return teamsFormFields.filter(field => isUndefined(field.featureFlag) || featureFlags[field.featureFlag])
  },[teamsFormFields, featureFlags]);

  const _afterClose = () => {
    afterClose(createdTeam.current);
  }

  const onSubmit = async () => {
    const valid = await form.validateFields();
    if (valid) {
      setLoading(true);
      try {
        const data = form.getFieldsValue();
        if (data?.name && typeof data.name === 'string') {
          data.name = data.name.trim();
        }
        KEYS.forEach(key => {
          if (data[key]) {
            data[key] = { id: data[key] };
          }
        });

        data.accessOnAllBusinessUnits = accessOnAllBusinessUnits;
        const {
          channels: [
            channelId
          ] = [],
          workspaceId,
          msTeamsLoginId,
          msTeamsTeam,
          msTeamsChannel
        } = data;

        if(!isUndefined(workspaceId) && !isUndefined(channelId)){
          const channelDetails = find(slackChannelOptions, { value : channelId});
          data.channels = {
            [channelId]: channelDetails?.label || channelId,
          };
        }

        if(!isUndefined(msTeamsLoginId) && !isUndefined(msTeamsTeam) && !isEmpty(msTeamsChannel)){
          const msTeamName = find(msTeamOptions, { value : msTeamsTeam}).label;
          data.msTeamsTeam = { id: msTeamsTeam, name: msTeamName }
          
          data.msTeamsChannel = map(msTeamsChannel, msChannel => {
            const msChannelName = find(msTeamsChannelsOptions, { value : msChannel}).label;
            return { id: msChannel, name: msChannelName}
          })
        }
        let method = 'POST';
        let funcName = 'saveTeam';
        if (editMode) {
          method = 'PUT';
          data.id = team.id;
          funcName = "saveTeamWithUser";
        }
        if (!isEmpty(data.members)) {
          const members = [];
          data.members.forEach(m => {
            if (m.user && m.role) {
              members.push({
                user: { id: m.user},
                role: m.role,
                disableLogin: m.disableLogin?? false
              })
            }
          })
          data.members = members;
        }

        (data.properties || []).forEach((property, index) => {
          (property.productSubProductMap || []).forEach(productSubMap => {
            const allAvailableProductsAndSubProducts = productsAndSubProductsRef.current[index]?.getAllListingData();
            // -- ENG-32935: pass accessOnAllSubProduct as true if user selects all subproducts available under particular product
            const subproductsList = _.find(allAvailableProductsAndSubProducts, { id: productSubMap.product })?.subProducts || [];
            productSubMap.accessOnAllSubProduct = productSubMap.accessOnAllSubProduct || subproductsList.length === productSubMap.subProduct.length;
          })
        })

        data.approvalWorkflow = {
          approvers: _.map(data.approvers, (entry, index) => {
            const userData = _.find(approversRef.current, { value: entry });
            const isEmail = typeof entry === "string" && entry.includes("@");
            return ({
              [isEmail ? "externalReviewerEmail" : "reviewerId"]: entry,
              reviewerType: isEmail ? "EXTERNAL_USER" : userData?.type || "USER",
              sequence: index
            })
          }),
          subscriberUserIds: data.subscriberUserIds
      }

        const _team = await TeamsAPI[funcName](_.omit(data, ['approvers', 'subscriberUserIds', "communication", "ownerShip", "scopeOfAccess"]), method).promise;
        createdTeam.current = _team;
        message.success(<><Vocabulary name="team" /> {editMode ? 'Updated' : 'Created'} successfully.</>);
        restProps.fetchListing && restProps.fetchListing();
      } catch (e) {
        setLoading(false);
        console.error(e);
        throw e;
      }
    }
  }

  const baseModalProps = {
		title: <>{editMode ? "Edit" : "Add"} <Vocabulary name="team" /></>,
		width: MODAL_SIZE_EXTRA_LARGE,
		okText: <>{editMode ? 'Save' : 'Add New'} <Vocabulary name="team" /></>,
		onOk: async () => {
			await onSubmit();
		}
	};

  return (
    <BaseModal
      ref={ref}
      modalProps={baseModalProps}
      afterClose={_afterClose}
      >
        <Spin spinning={loading} indicator={<AppLoader style={{ marginTop: 0 }} />}>
          <Router>
            <Row className='m-b-sm'>Add multiple Organizations and <Vocabulary name="product" isPlural={true} /> while adding the new <Vocabulary name="team" isLowerCase={true}/>.</Row>
            <Form 
              layout="vertical"
              form={form}
              initialValues={initialFormValues}
              className="team-form"
            >
              {generateFormFields(fields)}
            </Form>
          </Router>
        </Spin>
    </BaseModal>
  );
});

const validate = (name = "") => {
  const { promise } = TeamsAPI.validate({ name });
  return promise;
};

const checkForAdminOrModifyPermission = ({currentField, activeUser, editMode, permissionType="TEAMS"}) => {
  if(isUndefined(currentField) || !isNumber(currentField.businessUnitId) || isSuperOrTenantAdminOrTenantRole() || !activeUser) { // This means no BU is selected
    return true;
  }
  const teamLevelBuData = getTeamFormBu(currentField, activeUser);
  const role = activeUser?.tenantRole || activeUser?.role || teamLevelBuData?.role;
   // we considering single for now there will be an edge case when the same bu is there in multiple teams with different team role...

  if (hasPermission(editMode ? PermissionSetMap[`UPDATE_${permissionType}`] : PermissionSetMap[`CREATE_${permissionType}`]) && role) {
    const teamInfo = get(activeUser, 'teamInfo') || []; // since teamInfo value is null the deafult value will not be returned
    const buIds = getAllBUsIdsFromTeams(teamInfo);
    if (difference([currentField.businessUnitId], buIds).length === 0) {
      return true;
    }
  }
  return ([ROLE_SUPERADMIN, ROLE_TENANTADMIN, Admin].indexOf(role) !== -1);
};

const getBuMergeDataFromMultipleTeams = (currentField, activeUser) => {
  const teamInfos = (activeUser?.teamInfo || []).filter(team => {
    return team.properties.findIndex(property => property.buId === currentField.businessUnitId) !== -1;
  });
  const teamInfo = teamInfos.reduce((acc, curr) => { // if multiple teams has same bu's then merge the bu data.
    const buData = (curr.properties || []).find(property => property.buId === currentField.businessUnitId);
    if (!isEmpty(buData)) {
      Object.assign(acc, { ...buData , productSubProductMap: [...acc.productSubProductMap, ...(buData.productSubProductMap || [])], accessOnAllProduct: acc.accessOnAllProduct || buData.accessOnAllProduct});
    }
    return acc;
  }, { productSubProductMap: []});
  teamInfo.productSubProductMap = uniqBy(teamInfo.productSubProductMap, 'product');
  return teamInfo;
}

const hasAllProductAccessOnSelectedBU = (currentField, activeUser) => {
  if(isSuperOrTenantAdminOrTenantRole() || isUndefined(currentField)) { // super/tenant admin/tenant role should be able to edit any account
    return true
  }
  const selectedBU = getBuMergeDataFromMultipleTeams(currentField, activeUser);
  return (!isEmpty(activeUser?.tenantRole) || (selectedBU && selectedBU?.accessOnAllProduct)); // If user has tanant level role or all product access, it has superior product access
};

export const getHasAccessProductIDs = ( currentField, activeUser) => {
  if (isUndefined(currentField)) {
    return [];
  }
  if (hasAllProductAccessOnSelectedBU(currentField, activeUser)) {
    return [];
  }
  const buLevelData =  getBuMergeDataFromMultipleTeams(currentField, activeUser);
  const products = map(buLevelData?.products, (entry) => entry.product);
  return  products;
}

const getTeamFormBu = (currentField, activeUser) => {
  const teamInfo = activeUser?.teamInfo || [];
  return teamInfo.find(team => {
    return team?.properties.findIndex(property => property.buId === currentField.businessUnitId) !== -1;
  }) || {}; // return empty object if its undefined
}

const checkRecordAndActiveUserPermission = ({currentField, activeUser}) => {
  if(isUndefined(currentField) || !isNumber(currentField.businessUnitId) || isSuperOrTenantAdminOrTenantRole() || !activeUser) { // This means no BU is selected
    return true;
  }
  const teamLevelBuData = getTeamFormBu(currentField, activeUser);
  const role = activeUser?.tenantRole || activeUser?.role || teamLevelBuData?.role; // we considering single for now there will be an edge case when the same bu is there in multiple teams with different team role...

  return ([ROLE_SUPERADMIN, ROLE_TENANTADMIN, Admin].indexOf(role) !== -1);
}

const getAllBUsIdsFromTeams = (teamInfo) => {
  return teamInfo.reduce((acc, curr) => {
    if (!isEmpty(curr.properties)) {
      const ids = curr.properties.map(property => property.buId);
      ids.length > 0 && acc.push(...ids);
    }
    return acc;
  }, []);
}

const checkProductsDifferenceForAllBus = (teamInfo, record, activeUser) => {
  const productAccessDifferenceForAllBUs = map(record.properties, (recordBU) => {
    const _recordBU = {...recordBU, businessUnitId: recordBU.businessUnit?.id };
      if (!checkRecordAndActiveUserPermission(activeUser, _recordBU, true)) {
          return false; // If not admin in current BU, no need to check product access
      }
      let myBuLevelData;
      teamInfo.some(team => {
        myBuLevelData = find(team.properties, property => property.buId === _recordBU.businessUnitId);
        return myBuLevelData
      });
      // check if user has all product access or current record has all product access
      if ((myBuLevelData && myBuLevelData.accessOnAllProduct) || recordBU.accessOnAllProduct) {
        return true;
      }
      // check product level access
      const myProductIds = map(myBuLevelData?.products, (entry) => entry.product);
      const recordProductIds = map(_recordBU?.products, (entry) => entry.product);
      return difference(recordProductIds, myProductIds).length === 0;
  });
  return productAccessDifferenceForAllBUs.some(t => t);
}

const getDifferenceFromBUsAndProducts = (record, activeUser) => {
  const teamInfo = get(activeUser, 'teamInfo') || []; // since teamInfo value is null the deafult value will not be returned
  const buIds = getAllBUsIdsFromTeams(teamInfo) // get all BU's ids from the current user teamsInfo array.
  const recordBUIds = map(record.properties, 'businessUnit.id'); // get all Bu's ids from the record properties its has Bu's and Product.
  if (difference(recordBUIds, buIds)) {
      return checkProductsDifferenceForAllBus(teamInfo, record, activeUser); // If there are difference in Bu's ids than check for the productSubProductMap.
  }
  return false;
}

const RenderMemberRole = ({form, item, roleOptions, handleMemberChange, activeUserRole, setIsEditing }) => {
  const [showSelect, setShowSelect] = useState(false); 
  const [role, setRole] = useState(item.role)

  const handleCancel = useCallback(() => {
    setShowSelect(false);
    setIsEditing(item.user);
  }, [item.user, setIsEditing])

  const handleSaveChange = useCallback(() => {
    if (role !== item.role) {
      const members = form.getFieldValue('members');
      const index = members.findIndex(m => m.user === item.user);
      members[index].role = role;
      form.setFieldsValue({members});
      handleMemberChange({ value: role, index, type: 'role'})
    }
    handleCancel();
  }, [role, item, form, handleMemberChange, handleCancel])

  const getRoles = useMemo(() => {
    const options = [...roleOptions]
    const activeRole = activeUserRole[item.user];
    if (activeRole) {
      const hasOpts = options.find(option => option.id === activeRole.id);
      if (!hasOpts) {
        options.push({...activeRole, disabled: true});
      }
    }
    return  getRoleOptions(options);
  }, [roleOptions, activeUserRole, item])

  if (item.role && !showSelect) {
    return (
      <>
        <Typography.Text>{item.role}</Typography.Text> <Tooltip title="Edit"><EditPencilIcon  onClick={() => {
          setShowSelect(true);          
          setIsEditing(item.user, true);
        }}/></Tooltip>
      </>
    )
  }

  return (
    <div className='d-flex' style={{alignItems: 'center'}}>
      <Select
        size="small"
        style={{width: '80%'}}
        showSearch
        onChange={setRole}
        value={role}
        dropdownMatchSelectWidth={false}
        options={getRoles}
        filterOption={(input, option) => {
          return (option.value || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
        }}
      />
      <CheckCircleOutlined onClick={handleSaveChange} disabled={item.role === role}  className='clickable primary m-l-sm m-r-sm' style={{fontSize: 20}}/>
      <CloseCircleOutlined onClick={handleCancel} className='clickable danger'  style={{fontSize: 20}}/>
    </div>
  )
}

const AddMembers = ({setIsEditing, form, handleMemberChange, activeUserOptions, teamLevelUserOptions, selectedMembers, activeUserRole, roleOptions, onRemoveMembers, formData}) => {
  const [searchText, setSearchText] = useState('');
  const [memberlistVisible, setMemberListVisible] = useState(false);

  const handleSearchChange = useCallback(({ currentTarget }) => {
    setSearchText(currentTarget.value);
    setMemberListVisible(true);
  }, []);

  const handleRemove = useCallback((value) => {
    const members = form.getFieldValue('members');
    form.setFieldsValue({members: members.filter(m => m.user !== value)});
    onRemoveMembers();
    setIsEditing(value);
  }, [form, onRemoveMembers, setIsEditing])

  const memberMenu = useMemo(() => {
    return (
      <MemberMenu 
        activeUserOptions={activeUserOptions}
        teamLevelUserOptions={teamLevelUserOptions}
        selectedMembers={selectedMembers}
        form={form}
        handleMemberChange={handleMemberChange}
        handleRemove={handleRemove}
        searchText={searchText}
        setIsEditing={setIsEditing}
      />
    )
  }, [form, activeUserOptions, teamLevelUserOptions, selectedMembers, handleMemberChange, handleRemove, searchText, setIsEditing])

  const memberList = useMemo(() => {
    return (
      <Col span={24}>
        <List
          size="small"
          dataSource={formData.members}
          renderItem={ item => {
              const member = activeUserOptions.find(activeUser => activeUser.id === item.user)
              return (
                <List.Item key={item.user}>
                  <Row gutter={BASE_GUTTER} className='w-100 m-b-0' style={{alignItems: 'center'}}>
                    <Col className='p-b-0' span={11}>
                      <User userEmail={member?.email} />
                    </Col>
                    <Col className='p-b-0' span={11}>
                      <RenderMemberRole setIsEditing={setIsEditing} form={form} item={item} roleOptions={roleOptions} handleMemberChange={handleMemberChange} activeUserRole={activeUserRole}/>
                    </Col>
                    <Col span={2} className="text-right p-b-0">
                      <Tooltip title="Remove" placement='top'>
                          <DeleteIcon className="danger" onClick={() => handleRemove(item.user)}/>
                      </Tooltip>
                    </Col>
                  </Row>
                </List.Item>
              )
            }
          }
        />
      </Col>
    )
  }, [formData.members, activeUserOptions, setIsEditing, form, roleOptions, handleMemberChange, activeUserRole, handleRemove])

  return (
      <Collapse defaultActiveKey={'members'} bordered={false} className={`m-b-sm list-collapse`}>
        <Collapse.Panel header={<Typography.Text strong>Add Members</Typography.Text>} key={'members'} className="no-border">
          <Row gutter={BASE_GUTTER} style={{backgroundColor: '#fff', padding: 16}}>
            <Col span={10}>
              <Dropdown overlay={memberMenu} trigger={['click']} visible={memberlistVisible} onVisibleChange={setMemberListVisible}>
                <Input onChange={handleSearchChange} value={searchText}  suffix={  searchText ? <CloseIcon onClick={() => setSearchText('')} /> : <SearchIcon />  } placeholder={`Search member here`}/>
              </Dropdown>
            </Col>
            {
              isEmpty(formData.members) && <Col span={24} className="text-center">No Members</Col>
            }
            {
              !isEmpty(formData.members) && memberList
            }
          </Row>
        </Collapse.Panel>
    </Collapse>
  )
}

const MemberMenu = ({form, activeUserOptions, teamLevelUserOptions, selectedMembers, handleMemberChange, handleRemove, searchText, setIsEditing }) => {

  const onSelectItems = useCallback((event) => {
    event.stopPropagation();
    let addFlag = false;
    const members = form.getFieldValue('members');
    const value = event.target["data-value"]
    if (event.target.checked) {
      const member = activeUserOptions.find(activeUser => activeUser.id === value);
      if (member) {
        members.push({ role: '', user: value});
        addFlag = true;
      }
    }
    if (addFlag) {
      form.setFieldsValue({members: uniqBy(members, 'user')});
      handleMemberChange({value, index: members.length - 1, type: 'user'})
      setIsEditing(value, true);
    } else {
      handleRemove(value);
    }
  }, [activeUserOptions, handleRemove, handleMemberChange, form, setIsEditing])

  const filteredData = useMemo(() => {
    return  teamLevelUserOptions.filter(user => !searchText || _.includes(user.name.toLowerCase(), searchText.toLowerCase()));
  }, [teamLevelUserOptions, searchText])
  
  return (
      <div className="custom-search-popover">
        {
          isEmpty(filteredData) && searchText && <Row gutter={BASE_GUTTER}><Col span={24}>Not found</Col></Row>
        }
        {
            filteredData.map((p, i) => (
              <Row key={p.value} gutter={BASE_GUTTER}>
                <Col span={24}  className="p-b-0">
                    <Checkbox checked={selectedMembers.includes(p.value)} data-value={p.value} onChange={onSelectItems}>{p.name}</Checkbox>
                </Col>
            </Row>
            ))
        }
      </div>
  )
}

const OwnerShip = ({ form, initialFormValues, activeUserOptions, tenantLevelUserOptions, selectedMembers, team, buOptions, formData }) => {
  const [ownerTab, setOwnerTab] = useState('ownerShip');
  const ownerTabRef = useRef()

  useEffect(() => {
    if (!ownerTabRef.current && form) {
      setOwnerTab()
    }
  }, [form]);

  // only show Account Admins and members of the current team
  const memberOptions = useMemo(() => {
    return [...tenantLevelUserOptions, ...activeUserOptions.filter(user => selectedMembers.includes(user.id))];
  }, [activeUserOptions, tenantLevelUserOptions, selectedMembers])

  const fields = useMemo(() => {
    return [
      {
        name: 'businessOwner',
        colSpan: 8,
        label: <Vocabulary name="businessOwner" />,
        type: FieldTypes.SELECT,
        options: memberOptions,
        placeholder: <>Select <Vocabulary name="businessOwner" /></>,
        props: {
          allowClear: true
        }
      },
      {
        name: 'securityOwner',
        colSpan: 8,
        label: <Vocabulary name="securityOwner" />,
        type: FieldTypes.SELECT,
        options: memberOptions,
        placeholder: <>Select <Vocabulary name="securityOwner" /></>,
        props: {
          allowClear: true
        }
      },
      {
        name: 'engineeringOwner',
        colSpan: 8,
        label: <Vocabulary name="engineeringOwner" />,
        type: FieldTypes.SELECT,
        options: memberOptions,
        placeholder: <>Select <Vocabulary name="engineeringOwner" /></>,
        props: {
          allowClear: true
        }
      },
      {
        name: 'supportOwner',
        colSpan: 8,
        label: <Vocabulary name="supportOwner" />,
        type: FieldTypes.SELECT,
        options: memberOptions,
        placeholder: <>Select <Vocabulary name="supportOwner" /></>,
        props: {
          allowClear: true
        }
      },
      {
        name: 'complianceOwner',
        colSpan: 8,
        label: <Vocabulary name="complianceOwner" />,
        type: FieldTypes.SELECT,
        options: memberOptions,
        placeholder: <>Select <Vocabulary name="complianceOwner" /></>,
        props: {
          allowClear: true
        }
      }
    ];
  }, [memberOptions])

  return (
    <Collapse ref={ownerTabRef} activeKey={ownerTab} bordered={false} className={`m-b-sm list-collapse`} onChange={setOwnerTab}>
        <Collapse.Panel header={<Typography.Text strong>Ownership</Typography.Text>} key={'ownerShip'} className="no-border">
        <Form layout="vertical"
            form={form}
            initialValues={initialFormValues}
            className="team-form"
            >
              {generateFormFields(fields)}
          </Form>
          <AssociatedComponentWrapper team={team} buOptions={buOptions}/>
        </Collapse.Panel>
    </Collapse>
  )
}

const Communication = ({ form, initialFormValues, slackWorkspaceOptions, onClearWorkspaceId, handleWorkspaceChange, slackChannelOptions, slackChannelLoadingState, handleChannelSelection, msTeamConfigOptions, onClearMsTeamConfigId, handleMsTeamConfigChange, onClearMsTeam, handleMsTeamChange, msTeamOptions, msTeamLoadingState, msTeamsChannelsOptions, msTeamChannelLoadingState, featureFlags }) => {
  const [communicationTab, setCommunicationTab] = useState('communication');
  const communicationTabRef = useRef()
  const ARMORCODE_SLACK_APP = useEnvironmentVariables('REACT_APP_ARMORCODE_SLACK_APP');

  useEffect(() => {
    if (!communicationTabRef.current && form) {
      setCommunicationTab()
    }
  }, [form])


  const fields = useMemo(() => {
    return [
      ...[{
        name: 'emailAlias',
        colSpan: 8,
        label: "Email",
        placeholder: 'Enter email alias',
        help: "You can add only one email address, which can be an email alias as well.",
        rules:[emailValidator]
      },
      {
        name: '',
        colSpan: 16,
        type: FieldTypes.LABEL
      },
      {
        name: 'workspaceId',
        colSpan: 8,
        label: 'Slack Workspace',
        type: FieldTypes.SELECT,
        placeholder: 'Select a workspace',
        options: slackWorkspaceOptions,
        props: {
          allowClear: true,
          onClear: onClearWorkspaceId,
          onChange: handleWorkspaceChange,
        },
      },
      {
        name: 'channels',
        colSpan: 8,
        label: 'Slack Channel',
        type: FieldTypes.SELECT,
        placeholder: 'Select a channel',
        disabled: !(form.getFieldValue('workspaceId')),
        options: slackChannelOptions,
        loading: slackChannelLoadingState,
        help: <ul>
                <li>Please enter a channel name as per your <Vocabulary name="team" isLowerCase={true} />/workspace.</li>
                <li>The <a href={`https://armorcode.slack.com/apps/${ARMORCODE_SLACK_APP}`} rel="noopener noreferrer" target="_blank">ArmorCode Slack App</a> needs to be added to the private channel before it can be configured here.</li>
              </ul>,
        props: {
          mode: 'tags',
          allowClear: true,
          onChange: handleChannelSelection,
        },
      },
      {
        name: '',
        colSpan: 8,
        type: FieldTypes.LABEL
      }],{
        name: "msTeamsLoginId",
        label: "MS Teams Config",
        type: FieldTypes.SELECT,
        colSpan: 8,
        options: msTeamConfigOptions,
        props: {
          allowClear: true,
          onClear: onClearMsTeamConfigId,
          onChange: handleMsTeamConfigChange,
        }
      },
      {
        name: "msTeamsTeam",
        label: "MS Team",
        type: FieldTypes.SELECT,
        colSpan: 8,
        disabled: !(form.getFieldValue('msTeamsLoginId')),
        loading: msTeamLoadingState,
        options: msTeamOptions,
        props: {
          allowClear: true,
          onClear: onClearMsTeam,
          onChange: handleMsTeamChange,
        }
      }, {
        name: "msTeamsChannel",
        label: "MS Teams Channels",
        type: FieldTypes.MULTI_SELECT,
        colSpan: 8,
        disabled: !(form.getFieldValue('msTeamsTeam')),
        loading: msTeamChannelLoadingState,
        options: msTeamsChannelsOptions,
        rules: [{
          validator: (_, values)=> {
            if(values && values.length > 20){
              return Promise.reject("Maximum 20 channels can be selected")
            }
            return Promise.resolve()
          }
        }]
      }
    ]
  }, [form, slackWorkspaceOptions, onClearWorkspaceId, handleWorkspaceChange, slackChannelOptions, slackChannelLoadingState, handleChannelSelection,msTeamConfigOptions, handleMsTeamConfigChange, msTeamOptions, handleMsTeamChange, onClearMsTeam, onClearMsTeamConfigId, msTeamLoadingState, msTeamsChannelsOptions, msTeamChannelLoadingState, ARMORCODE_SLACK_APP])

  return (
    <Collapse activeKey={communicationTab} bordered={false} className={`m-b-sm list-collapse`} onChange={setCommunicationTab}>
        <Collapse.Panel header={<Typography.Text strong>Communication</Typography.Text>} key={'communication'} className="no-border">
        <Form layout="vertical"
            form={form}
            initialValues={initialFormValues}
            className="team-form"
            >
              {generateFormFields(fields)}
          </Form>
        </Collapse.Panel>
    </Collapse>
  )
}

const ScopeOfAccess = ({ 
  form, initialFormValues, handleAllBusinessUnit, onRemoveItem, accessOnAllBusinessUnits, selectedBu, buOptions, activeUserBUs,
  handleBUChange, productsAndSubProductsRef, productAndSubProductChange, activeUserProducts, activeUser, editMode, handleCheckBoxChange,
  onAccessTypeChange, onGroupSelect
}) => {
  const fields = useMemo(() => {
    return [
      {
        name: 'properties',
        label: <div>
          <Tooltip title={isSuperOrTenantAdminOrTenantRole() ? undefined : "No Access"}>
            <Checkbox disabled={!isSuperOrTenantAdminOrTenantRole()} checked={accessOnAllBusinessUnits} className="m-t-0 m-l-m" onChange={handleAllBusinessUnit}>
                <Typography.Text>
                    All Organizations
                </Typography.Text>
            </Checkbox>
          </Tooltip>
        </div>,
        form: form,
        type: FieldTypes.LIST,
        buttonLabel: 'Add Organization',
        onRemoveItem: onRemoveItem,
        editMode: accessOnAllBusinessUnits,
        addButtonDisabled: checkAvailableOptionsAreSelected(selectedBu, buOptions, Object.values(activeUserBUs)) ,
        newRowDefaultValue: { ...initialValues.properties[0] },
        fieldRows: [
          [
            {
              name: 'businessUnitId',
              type: FieldTypes.SELECT,
              colSpan: 8,
              label: "Organization",
              props: {
                onChange: (value, field) => handleBUChange(value, field),
                  getFormListOptions: (fieldKey) => {
                    const activeUserBUsOpts = parseSelectOptions(activeUserBUs[fieldKey]);
                    const options = buOptions.map(o => { return {label: o.name, value: o.id, disabled: (o.disabled || selectedBu.includes(o.id))} })
                    activeUserBUsOpts.forEach(opt => {
                      const obj = find(options, { value: opt.value});
                      !obj && options.push({...opt, disabled: true});
                    });
                    return options
                  },
                  setExtraProps: (field, fieldKey) => {
                    const { key } = fieldKey
                    const configs = form.getFieldValue("properties") || [];
                    return { disabled: !checkForAdminOrModifyPermission({currentField: configs[key], activeUser, editMode})};
                  }
              },
              rules: [{ required: true, message: "Organization is required"}]
            },
            {
              name: 'productSubProductMap',
              label: "",
              colSpan: 13,
              render: (field) => {
                  return (
                    <TeamProductSubProductAccessField
                      field={field}
                      productSubProductFieldRef={(el) =>
                        (productsAndSubProductsRef.current[field.name] = el)
                      }
                      productAndSubProductChange={productAndSubProductChange}
                      onAccessTypeChange={onAccessTypeChange}
                      onGroupSelect={onGroupSelect}
                    />
                  );
              },
              props: {
                setExtraProps: (field, fieldKey) => {
                  const { name } = fieldKey;
                  const configs = form.getFieldValue("properties") || [];
                  const fieldData = configs[name];
                  const buSelected = fieldData?.businessUnitId;
                  const val =  fieldData?.accessOnAllProduct;
                  const _activeUserProducts = activeUserProducts[buSelected];
                  const preSelectedData = {};
                  const preAccessOnAllProductAndSubProductsIds = [];
                  if (!_.isEmpty(_activeUserProducts)) {
                    _activeUserProducts.forEach(obj => {
                      const {product, subProduct} = obj;
                      const productId = product;
                      preSelectedData[productId] = subProduct;
                      if (obj.accessOnAllSubProduct) {
                        preAccessOnAllProductAndSubProductsIds.push(productId);
                      }
                    })
                  }
                  return {
                    disabled: (!checkForAdminOrModifyPermission({currentField: fieldData, activeUser, editMode}) || val), 
                    businessUnitId: buSelected,
                    preSelectedData,
                    preAccessOnAllProductAndSubProductsIds,
                    accessType: fieldData?.accessType,
                    groups: fieldData?.groups
                  }
                },
                setFormListItemProps: (field, fieldKey) => {
                  const { name } = fieldKey;
                  const configs = form.getFieldValue("properties") || [];
                  const config = configs[name];
                  const props = {};
                  if (!config?.accessOnAllProduct) {
                    props.rules = [
                      {
                        validator: () => {
                          if(isEmpty(config.productSubProductMap) && isEmpty(config.groups)){
                            return Promise.reject(
                              config.accessType === ACCESS_TYPE_GROUPS ? (
                                <>Select <Vocabulary name= "group"/>(s)</>
                              ) : (
                                <>
                                  Select{" "}
                                  <Vocabulary name="product" isPlural={true} />/
                                  <Vocabulary
                                    name="subProduct"
                                    isPlural={true}
                                  />
                                </>
                              )
                            );
                          }
                          return Promise.resolve();
                        }
                      }
                    ]
                  } 
                  // if all access or products selected or groups selected then hide error
                  if(config?.accessOnAllProduct || !isEmpty(config?.productSubProductMap) || !isEmpty(config?.groups)) {
                    props.validateStatus = "success";
                    props.help = "";
                  }
                  return props
                }
              }
            },
            {
              name: 'accessOnAllProduct',
              type: FieldTypes.BOOLEAN,
              colSpan: 3,
              label: "All",
              props: {
                onChange: handleCheckBoxChange,
                setExtraProps: (field, fieldKey) => {
                  const { name } = fieldKey
                  const configs = form.getFieldValue("properties") || [];
                  return {disabled: (!hasAllProductAccessOnSelectedBU(configs[name], activeUser) || !checkForAdminOrModifyPermission({currentField: configs[name], activeUser, editMode}))};
                }
              }
            }
          ] 
        ]
      }
    ]
  }, [form, handleAllBusinessUnit, onRemoveItem, accessOnAllBusinessUnits, selectedBu, buOptions, activeUserBUs,
    handleBUChange, productsAndSubProductsRef, productAndSubProductChange, activeUserProducts, activeUser, editMode, handleCheckBoxChange,
    onAccessTypeChange, onGroupSelect
  ])

  return (
    <Collapse defaultActiveKey={'scopeOfAccess'} bordered={false} className={`m-b-sm list-collapse`}>
        <Collapse.Panel header={<Typography.Text strong>Scope Of Access</Typography.Text>} key={'scopeOfAccess'} className="no-border">
        <Form layout="vertical"
            form={form}
            initialValues={initialFormValues}
            className="team-form"
            >
              {generateFormFields(fields)}
          </Form>
        </Collapse.Panel>
    </Collapse>
  )
}

const AssociatedComponentWrapper = ({ team, buOptions }) => {
  const [loading, setLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [subProductList, setSubProductList] = useState([]);

  useEffect(() => {
    if (team && !_.isEmpty(buOptions)) {
      setLoading(true);
      const cancelFns = [];

      /// Get associated product/subproduct IDs
      const { promise, cancel } = TeamsAPI.getAssociatedProductsAndSubProducts(team.id);
      cancelFns.push(cancel);

      promise
        .then(associates => {
          const associatedProducts = associates?.Products || [];
          const associatedSubProducts = associates?.SubProducts || [];

          // Extract IDs
          const productIds = associatedProducts.map(p => p.id);
          const subProductIds = associatedSubProducts.map(sp => sp.id);
          const buIds = buOptions.map(bu => bu.id);

          // Handle empty case - no associations
          if (productIds.length === 0 && subProductIds.length === 0) {
            setProductList([]);
            setSubProductList([]);
            return;
          }

          // Fetch ONLY the associated products/subproducts
          const promiseArr = [];

           // Only fetch products if we have product IDs
          if (productIds.length > 0) {
            const { promise: productPromise, cancel: productCancel } = ProductAPI.getAllQuick_V2({
              buId: buIds,
              ids: productIds,
              pageSize: productIds.length
            });
            cancelFns.push(productCancel);
            promiseArr.push(productPromise);
          } else {
            promiseArr.push(Promise.resolve([]));
          }

          if (subProductIds.length > 0) {
            const { promise: subProductPromise, cancel: subProductCancel } = SubProductAPI.getAllQuick_V2({
                buId: buIds,
                subProductIds: subProductIds,
              })
            cancelFns.push(subProductCancel);
            promiseArr.push(subProductPromise);
    
          } else {
            promiseArr.push(Promise.resolve([]));
          }

          return Promise.all(promiseArr).then(responses => {
            const [productsResponse, subProductResponses] = responses;
            if (productsResponse.data) {
              const { data } = productsResponse;
              setProductList(data);
            }

            if (subProductResponses) {
              const { data } = subProductResponses;
              setSubProductList(data);
            }
          });
        })
        .catch(error => {
          console.error('Error fetching associated products/subproducts:', error);
          setProductList([]);
          setSubProductList([]);
        })
        .finally(() => {
          setLoading(false);
        });

      return () => cancelFns.forEach(fn => fn());
    }
  }, [team, buOptions])

  return (
    <Spin spinning={loading} indicator={<AppLoader style={{ marginTop: 0, fontSize: 20 }} />}>
      <AssociatedComponent data={productList} type="product" title={<Vocabulary  name="product"  isPlural={true} />} />
      <AssociatedComponent data={subProductList} type="subProduct" title={<Vocabulary  name="subProduct" isPlural={true} />}/>
    </Spin>
  )
}

const AssociatedComponent = ({ data=[], title, type }) => {
  const [searchBoxText, setSearchBoxText] = useState('');

  const handleSearchChange = useCallback(({ currentTarget }) => {
    setSearchBoxText(currentTarget?.value)
  }, [])

  const filteredData = useMemo(() => {
    const matchFilter = new RegExp(searchBoxText, 'i');
    return  data.filter(filteredList => !searchBoxText || matchFilter.test(filteredList.name));
  }, [data, searchBoxText])

  return (
    <Collapse bordered={false} className={`m-b-sm`} style={{backgroundColor: '#FFFFFF'}}>
        <Collapse.Panel header={<Typography.Text strong>Owned {title} ({data.length})</Typography.Text>} key={type} className="no-border">
          <Row gutter={BASE_GUTTER}>
            <Col span={10}>
              <Input autoFocus  onChange={handleSearchChange} value={searchBoxText}  suffix={searchBoxText ? <CloseIcon onClick={() => setSearchBoxText('')} /> : <SearchIcon /> } placeholder={`Search by ${getVocabulary(type)}`}/>
            </Col>
          </Row>
          <Row>
            <Col span={12}>
                {
                  isEmpty(data) && !searchBoxText && <Typography.Text>No Associate {title}</Typography.Text>
                }
                {
                  isEmpty(filteredData) && searchBoxText && <Typography.Text>Not found</Typography.Text>
                }
                {
                  !isEmpty(filteredData) && (
                    <List
                      size="small"
                      dataSource={filteredData}
                      renderItem={item => <List.Item>
                          <Popover overlayStyle={{ width: 400 }} placement='right' content={<CustomPopOver item={item} type={type} />}>
                            {item.name}
                          </Popover>
                        </List.Item>}
                    />
                  )
                }
              </Col>
          </Row>
        </Collapse.Panel>
    </Collapse>
  )
}

const CustomPopOver = ({item, type }) => {
  const [loading, setLoading] = useState(false);
  const [itemDetails, setItemDetails] = useState(null);

  useEffect(() => {
    if (!item?.id) {
      return;
    }

    setLoading(true);
    const apiCall = type === 'product'
      ? ProductAPI.v2.get(item.id)
      : SubProductAPI.get(item.id);

    const { promise, cancel } = apiCall;

    promise
      .then(response => {
        setItemDetails(response);
      })
      .catch(error => {
        console.error(`Error fetching ${type} details:`, error);
      })
      .finally(() => {
        setLoading(false);
      });

    return cancel;
  }, [item?.id, type]);

  const businessOwner = CellRenderers.VALUE_OR_NA(itemDetails?.businessOwner?.name || itemDetails?.teamDto?.businessOwner?.name );
  const securityOwner = CellRenderers.VALUE_OR_NA(itemDetails?.securityOwner?.name || itemDetails?.teamDto?.securityOwner?.name);
  const engineeringOwner = CellRenderers.VALUE_OR_NA(itemDetails?.engineeringOwner?.name || itemDetails?.teamDto?.engineeringOwner?.name);
  const supportOwner = CellRenderers.VALUE_OR_NA(itemDetails?.supportOwner?.name || itemDetails?.teamDto?.supportOwner?.name);
  const complianceOwner = CellRenderers.VALUE_OR_NA(itemDetails?.complianceOwner?.name || itemDetails?.teamDto?.complianceOwner?.name)

  if (loading) {
    return <Spin indicator={<AppLoader style={{ marginTop: 0, fontSize: 14 }} />} />;
  }

  return (
    <Row gutter={BASE_GUTTER}>
        <Col span={12}>
          <RenderPopoverValue label={'businessOwner'} value={businessOwner} />
        </Col>
        <Col span={12}>
          <RenderPopoverValue label={'securityOwner'} value={securityOwner} />
        </Col>
        <Col span={12}>
          <RenderPopoverValue label={'engineeringOwner'} value={engineeringOwner} />
        </Col>
        <Col span={12}>
          <RenderPopoverValue label={'supportOwner'} value={supportOwner} />
        </Col>
        <Col span={12}>
          <RenderPopoverValue label={'complianceOwner'} value={complianceOwner} />
        </Col>
    </Row>
  )
}

const RenderPopoverValue = ({label, value}) => {
  return (
      <>
        <Typography.Text strong><Tooltip placement='topLeft' title={value === 'N/A' ? null : value}><div className='text-ellipsis'>{value}</div></Tooltip></Typography.Text>
        <Vocabulary name={label} />
      </>
  )
}

export {
  getDifferenceFromBUsAndProducts
}
